// UserContext.tsx
import { FirebaseError } from 'firebase/app';
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithPopup,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailLink,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  signOut,
  User,
  ConfirmationResult,
} from 'firebase/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { PersonProperties } from '../common/analytics/analytics-properties';
import { AnalyticsEventName, AnalyticsUserProperties } from '../common/analytics/types';
import { aliasUser, identifyUser, trackEvent, trackPersonProperties } from '../common/analytics/utils';
import { LoginMethod, LoginState } from '../common/login';
import { auth } from '../firebaseConfig';

// Extend Window interface for Firebase reCAPTCHA
declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

interface UserContextProps {
  user: User | null;
  idToken: string | null;
  loginState: LoginState;
  loginWithGoogle: () => Promise<void>;
  sendSignInLinkToEmail: (email: string) => Promise<void>;
  signInWithPhone: (phoneNumber: string) => Promise<ConfirmationResult>;
  signInWithCustomToken: (customToken: string) => Promise<User>;
  logout: () => Promise<void>;
}

const UserContext = createContext<UserContextProps>({
  user: null,
  idToken: null,
  loginState: LoginState.Init,
  loginWithGoogle: async () => {},
  sendSignInLinkToEmail: async () => {},
  signInWithPhone: async () => {
    throw new Error('signInWithPhone is not implemented');
  },
  signInWithCustomToken: async () => {
    throw new Error('signInWithCustomToken not implemented');
  },
  logout: async () => {},
});

const EMAIL_FOR_SIGNIN_KEY = 'emailForSignIn';
const ANON_UID_KEY = 'anonUid';

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loginState, setLoginState] = useState<LoginState>(LoginState.Init);
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loginMethod, setLoginMethod] = useState<LoginMethod | null>(null);

  // Handle login state changes
  // - set the user
  // - set the idToken for the useGetFollows hook
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      let idToken = null;
      if (user) {
        setLoginState(LoginState.LoggedIn);
        if (!user.isAnonymous) {
          try {
            idToken = await user.getIdToken();
          } catch (error) {
            console.error('Error getting ID token:', error);
          }
        }
      } else {
        if (loginState === LoginState.Init) {
          setLoginState(LoginState.NotLoggedInYet);
        } else if (loginState === LoginState.LoggedIn) {
          setLoginState(LoginState.LoggedOut);
        }
      }
      setUser(user);
      setIdToken(idToken);
    });

    return () => unsubscribe();
  }, [loginState, loginMethod]);

  // Handle analytics identification
  useEffect(() => {
    if (!user) {
      return;
    }
    const userProperties: AnalyticsUserProperties = {};
    // Check to avoid overwriting known properties with null values
    if (user.email) {
      userProperties.email = user.email;
    }
    if (user.displayName) {
      userProperties.name = user.displayName;
    }
    identifyUser(user.uid, userProperties);
  }, [user]);

  // Handle anonymous login
  useEffect(() => {
    if (loginState === LoginState.NotLoggedInYet || loginState === LoginState.LoggedOut) {
      loginAnonymously();
    }
  }, [loginState]);

  // Handle sign in with email link
  useEffect(() => {
    const handleSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        setLoginState(LoginState.ProcessingLogin);
        let email = window.localStorage.getItem(EMAIL_FOR_SIGNIN_KEY);
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }
        if (!email) {
          // Give up and let it become an anonymous user
          setLoginState(LoginState.NotLoggedInYet);
          return;
        }
        try {
          await signInWithEmailLink(auth, email, window.location.href);
          window.localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY);
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error signing in with email link', error);
          setLoginState(LoginState.Error);
        }
      }
    };
    handleSignIn();
  }, []);

  // Store anonymous UID for PostHog aliasing
  useEffect(() => {
    if (user && user.isAnonymous) {
      localStorage.setItem(ANON_UID_KEY, user.uid);
    }
  }, [user?.isAnonymous]);

  // Alias anonymous UID to permanent UID in PostHog after upgrade
  useEffect(() => {
    if (user && !user.isAnonymous) {
      const anonUid = localStorage.getItem(ANON_UID_KEY);
      if (anonUid && anonUid !== user.uid) {
        aliasUser(user.uid, anonUid);
        localStorage.removeItem(ANON_UID_KEY);
      }
    }
  }, [user?.isAnonymous]);

  // Set login method when user is fully logged in
  useEffect(() => {
    if (!user || loginState !== LoginState.LoggedIn) {
      return;
    }
    if (user.isAnonymous) {
      setLoginMethod(LoginMethod.Anonymous);
    } else {
      // Skip setting login method if it's already set (e.g., by custom token sign-in)
      // This preserves LoginMethod.Invitation set during custom token authentication
      if (loginMethod !== null) {
        return;
      }

      const providers = user.providerData.map((provider) => provider.providerId);
      if (providers.includes('google.com')) {
        setLoginMethod(LoginMethod.Google);
      } else if (providers.includes('password')) {
        setLoginMethod(LoginMethod.Email);
      } else if (providers.includes('phone')) {
        setLoginMethod(LoginMethod.Phone);
      } else {
        // For custom token users or other unrecognized providers, default to Email
        console.warn('Unknown provider data:', providers, 'defaulting to Email login method');
        setLoginMethod(LoginMethod.Email);
      }
    }
  }, [user, loginState, loginMethod]);

  // Track login info in the user profile
  // This is useful to quickly segment users based on their last login state
  useEffect(() => {
    if ([LoginState.Init, LoginState.NotLoggedInYet, LoginState.ProcessingLogin].includes(loginState) || !loginMethod) {
      return;
    }
    const personProperties: PersonProperties = {
      loginMethod,
      loginState,
    };
    trackPersonProperties(personProperties);
  }, [loginState, loginMethod]);

  // Track login state changes
  useEffect(() => {
    if (loginState === LoginState.Init) {
      return;
    }
    trackEvent(AnalyticsEventName.LoginStateChanged, { loginState });
  }, [loginState]);

  // Track login method changes
  useEffect(() => {
    if (loginMethod === null) {
      return;
    }
    trackEvent(AnalyticsEventName.LoginMethodChanged, { loginMethod });
  }, [loginMethod]);

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setLoginState(LoginState.ProcessingLogin);
      const user = auth.currentUser;
      if (user && user.isAnonymous) {
        try {
          await linkWithPopup(user, provider);
        } catch (error: unknown) {
          const firebaseError = error as FirebaseError;
          if (firebaseError.code !== 'auth/credential-already-in-use') {
            throw error;
          }
          const credential = GoogleAuthProvider.credentialFromError(firebaseError);
          if (credential) {
            await signInWithCredential(auth, credential);
          } else {
            throw error;
          }
        }
      } else {
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error('Error logging in with Google:', error);
      setLoginState(LoginState.Error);
    }
  };

  const loginAnonymously = async () => {
    try {
      setLoginState(LoginState.ProcessingLogin);
      await signInAnonymously(auth);
    } catch (error) {
      console.error('Error logging in anonymously:', error);
      setLoginState(LoginState.Error);
    }
  };

  const sendSignInLinkToEmailHandler = async (email: string) => {
    const redirectUrl = window.location.href;
    const actionCodeSettings = {
      url: redirectUrl,
      handleCodeInApp: true,
    };
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem(EMAIL_FOR_SIGNIN_KEY, email);
    } catch (error) {
      console.error('Error sending email link', error);
    }
  };

  const signInWithPhoneHandler = async (phoneNumber: string) => {
    try {
      // Set up reCAPTCHA verifier if it doesn't exist
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: (_response: unknown) => {
            // reCAPTCHA solved, you get the token here
          },
          'expired-callback': () => {
            // Reset the verifier
            window.recaptchaVerifier = undefined;
          },
          'error-callback': (error: unknown) => {
            // Added for more detailed error handling
            console.error('❌ reCAPTCHA error:', error);
            window.recaptchaVerifier = undefined;
          },
          // The 'render' callback
          render: (_widgetId: unknown) => {
            // Add custom class to body when reCAPTCHA is active
            document.body.classList.add('recaptcha-active');
          },
        });
      }

      // Ensure phone number is in international format
      let formattedPhone = phoneNumber;
      if (!phoneNumber.startsWith('+')) {
        // Assume US number if no country code, remove all non-digits first
        const digitsOnly = phoneNumber.replace(/\D/g, '');
        formattedPhone = '+1' + digitsOnly;
      }

      // Send SMS verification code
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);

      return confirmationResult;
    } catch (error) {
      console.error('❌ Error in signInWithPhoneHandler:', error);
      if (error instanceof FirebaseError) {
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
      }

      // Reset the reCAPTCHA verifier on error
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = undefined;
        // Remove the class when clearing verifier
        document.body.classList.remove('recaptcha-active');
      }

      throw error;
    }
  };

  const signInWithCustomTokenHandler = async (customToken: string): Promise<User> => {
    try {
      const userCredential = await signInWithCustomToken(auth, customToken);

      setLoginState(LoginState.LoggedIn);
      setLoginMethod(LoginMethod.Invitation);

      return userCredential.user;
    } catch (error) {
      console.error('❌ Error signing in with custom token:', error);
      setLoginState(LoginState.Error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setLoginMethod(null); // Clear login method on logout
      setLoginState(LoginState.LoggedOut);
    } catch (error) {
      console.error('Error logging out:', error);
      setLoginState(LoginState.Error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        idToken,
        loginState,
        loginWithGoogle,
        sendSignInLinkToEmail: sendSignInLinkToEmailHandler,
        signInWithPhone: signInWithPhoneHandler,
        signInWithCustomToken: signInWithCustomTokenHandler,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);

// Accept HMR updates to prevent context loss during Fast Refresh
if (import.meta.hot) {
  import.meta.hot.accept();
}
